import nanoid from "nanoid";
import {
	game
} from "../../Game"
import { disableCulling } from '../../utils/PIXICuller';
import { applyColorMatrix } from "./colorMatrixParser";
import { isBodyGroup, setBodyGroupOpacity } from "./groupEditing";
import { mirrorAttachedGraphicData, mirrorBodyData, mirrorGraphicData, scaleAttachedGraphicData, scaleBodyData, scaleGraphicData, useGroupedTextureScale } from "./groupScale";

const vec1 = new Box2D.b2Vec2();

// Clones of a group share everything but these. Shapes are stored at each clone's own cloneScale, mirrored where it's negative.
const INSTANCE_KEYS = ['x', 'y', 'rotation', 'ID', 'groups', 'refName', 'lockselection', 'bodyID', 'cloneId', 'cloneScale'];

// Settings each clone keeps for itself; an attached graphic still shares them.
const cloneSettings = type => {
	const editor = game.editor;
	if (type === editor.object_BODY) return ['visible', 'groupOpacity', 'awake'];
	const settings = ['visible', 'transparancy', 'mirrored', 'parallax', 'repeatTeleportX', 'repeatTeleportY'];
	if (type === editor.object_ANIMATIONGROUP) settings.push('fps', 'playing', 'loop');
	return settings;
}

// Signatures as of the last sync, and which instances took part in it.
let syncedSignatures = new Map();
let syncedInstances = new WeakSet();

const sharedData = (data, isClone) => {
	const editor = game.editor;
	const copy = editor.parseArrObject(JSON.parse(editor.serializeObject(data, true)));
	INSTANCE_KEYS.forEach(key => delete copy[key]);
	if (isClone) cloneSettings(copy.type).forEach(key => delete copy[key]);
	return copy;
}

const rootSprite = object => object.mySprite || object;

export const isCloneable = object => {
	const editor = game.editor;
	const sprite = rootSprite(object);
	if (!sprite.data || sprite.data.prefabInstanceName) return false;
	if (object.mySprite) return sprite.data.type === editor.object_BODY && isBodyGroup(object);
	return [editor.object_GRAPHICGROUP, editor.object_ANIMATIONGROUP].includes(sprite.data.type) && !sprite.myBody;
}

const instanceOf = sprite => {
	if (!sprite.data?.cloneId) return null;
	const object = sprite.data.type === game.editor.object_BODY ? sprite.myBody : sprite;
	return object && isCloneable(object) ? object : null;
}

export const getCloneSets = () => {
	const sets = new Map();
	game.editor.textures.children.forEach(sprite => {
		const object = instanceOf(sprite);
		if (!object) return;
		if (!sets.has(sprite.data.cloneId)) sets.set(sprite.data.cloneId, []);
		sets.get(sprite.data.cloneId).push(object);
	});
	return sets;
}

export const getClones = object => {
	const id = rootSprite(object).data?.cloneId;
	if (!id || !isCloneable(object)) return [];
	const clones = getCloneSets().get(id) || [];
	return clones.length > 1 ? clones : [];
}

// Copies of a group are clones of it. The original only joins the set once a copy is pasted,
// so copying alone doesn't change the level.
export const cloneIdForCopy = object => {
	if (!isCloneable(object)) return '';
	const sprite = rootSprite(object);
	if (sprite.data.cloneId) return sprite.data.cloneId;
	if (!sprite.pendingCloneId) sprite.pendingCloneId = nanoid(8);
	return sprite.pendingCloneId;
}

export const claimPendingClones = pasted => {
	const ids = new Set(pasted.filter(isCloneable).map(object => rootSprite(object).data.cloneId).filter(Boolean));
	if (!ids.size) return;
	game.editor.textures.children.forEach(sprite => {
		if (!sprite.pendingCloneId || !ids.has(sprite.pendingCloneId)) return;
		const object = sprite.data.type === game.editor.object_BODY ? sprite.myBody : sprite;
		if (object && isCloneable(object) && !sprite.data.cloneId) sprite.data.cloneId = sprite.pendingCloneId;
	});
}

export const unlinkClones = objects => {
	objects.forEach(object => {
		if (isCloneable(object)) rootSprite(object).data.cloneId = '';
	});
}

// What a clone shares, rescaled from its own scale by [scaleX, scaleY].
const sharedFor = (object, [scaleX, scaleY]) => {
	const editor = game.editor;
	const sprite = rootSprite(object);
	editor.updateObject(sprite, sprite.data);
	const data = sharedData(sprite.data, true);
	const matrix = [Math.abs(scaleX), 0, 0, Math.abs(scaleY)];
	const mirrors = [];
	if (scaleX < 0) mirrors.push(true);
	if (scaleY < 0) mirrors.push(false);
	if (!object.mySprite) {
		useGroupedTextureScale(data);
		scaleGraphicData(data, matrix);
		mirrors.forEach(horizontal => mirrorGraphicData(data, horizontal));
		return {data, texture: null};
	}
	let texture = null;
	if (object.myTexture) {
		editor.updateObject(object.myTexture, object.myTexture.data);
		texture = sharedData(object.myTexture.data);
		if (texture.graphicObjects) useGroupedTextureScale(texture);
		scaleAttachedGraphicData(texture, matrix);
		mirrors.forEach(horizontal => mirrorAttachedGraphicData(texture, horizontal));
	}
	scaleBodyData(data, matrix);
	mirrors.forEach(horizontal => mirrorBodyData(data, horizontal));
	return {data, texture};
}

const scaleOf = object => rootSprite(object).data.cloneScale || [1, 1];

// the shared part at scale 1, to compare clones of different sizes
const signatureOf = object => {
	const [scaleX, scaleY] = scaleOf(object);
	const signature = sharedFor(object, [1 / scaleX, 1 / scaleY]);
	// mirroring can land the offset angle on either side of ±π
	const texture = signature.texture;
	if (texture?.texturePositionOffsetAngle != null) {
		texture.texturePositionOffsetAngle = [Math.cos(texture.texturePositionOffsetAngle), Math.sin(texture.texturePositionOffsetAngle)];
	}
	return signature;
}

// float noise from rescaling and rounded saves isn't a change
const sameShared = (a, b) => {
	if (typeof a === 'number' && typeof b === 'number') return Math.abs(a - b) <= 1e-3 * Math.max(1, Math.abs(a), Math.abs(b));
	if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return a === b;
	const keys = Object.keys(a);
	return keys.length === Object.keys(b).length && keys.every(key => sameShared(a[key], b[key]));
}

const sharedForTarget = (target, source) => {
	const [sourceX, sourceY] = scaleOf(source);
	const [targetX, targetY] = scaleOf(target);
	return sharedFor(source, [targetX / sourceX, targetY / sourceY]);
}

const applyToGraphicGroup = (sprite, source) => {
	const editor = game.editor;
	const data = sprite.data;
	Object.assign(data, sharedForTarget(sprite, source).data);
	sprite.scale.x = data.mirrored ? -1 : 1;
	editor.updateGraphicGroupShapes(sprite);
	sprite.alpha = data.transparancy;
	if (!data.visible) disableCulling(sprite);
	sprite.renderable = data.visible;
	sprite.forceRenderable = data.visible;
	if (data.type === editor.object_ANIMATIONGROUP) {
		sprite.isAnimation = false;
		editor.initAnimation(sprite);
	} else {
		applyColorMatrix(sprite, data.colorMatrix);
	}
	sprite._cullingSizeDirty = true;
	sprite.x++;
	sprite.x--;
}

const applyToBody = (body, source) => {
	const editor = game.editor;
	const data = body.mySprite.data;
	const shared = sharedForTarget(body, source);
	Object.assign(data, shared.data);

	if (data.fixed) body.SetType(Box2D.b2_staticBody);
	else body.SetType(Box2D.b2_dynamicBody);
	editor.updateBodyFixtures(body);
	editor.setBodyCollision(body, data.collision);
	body.ResetMassData();
	body.SetFixedRotation(data.fixedRotation);
	body.SetBullet(data.bulletCollision);
	if (!data.optimizePhysics) {
		body.ignorePhysicsCuller = true;
		body.SetSleepingAllowed(false);
	} else {
		body.SetSleepingAllowed(true);
		delete body.ignorePhysicsCuller;
	}
	body.instaKill = data.instaKill;
	body.isVehiclePart = data.isVehiclePart;
	body.canHavePortal = data.canHavePortal !== false;

	editor.updateBodyShapes(body);
	editor.updateTileSprite(body, true);
	if (!data.visible) disableCulling(body.mySprite);
	body.mySprite.renderable = data.visible;

	if (!sameShared(body.myTexture ? sharedData(body.myTexture.data) : null, shared.texture)) {
		replaceAttachedGraphic(body, shared.texture);
	}
	if (body.myTexture) body.myTexture.renderable = data.visible;

	if (isBodyGroup(body)) setBodyGroupOpacity(body, data.groupOpacity);
	else body.mySprite.alpha = data.transparancy[0];
	editor.updateBodyPosition(body);
}

const replaceAttachedGraphic = (body, graphicData) => {
	const editor = game.editor;
	const container = editor.textures;
	const oldGraphic = body.myTexture;
	let index = container.getChildIndex(body.mySprite) + 1;
	const names = {groups: oldGraphic?.data.groups || '', refName: oldGraphic?.data.refName || ''};
	if (oldGraphic) {
		index = container.getChildIndex(oldGraphic);
		editor.removeTextureFromBody(body, oldGraphic);
		editor.deleteObjects([oldGraphic]);
	}
	if (!graphicData) return;

	const data = {...graphicData, ...names, x: 0, y: 0, rotation: 0, ID: 0};
	data.bodyID = container.getChildIndex(body.mySprite);
	const build = {
		[editor.object_TEXTURE]: obj => editor.buildTextureFromObj(obj),
		[editor.object_GRAPHIC]: obj => editor.buildGraphicFromObj(obj),
		[editor.object_GRAPHICGROUP]: obj => editor.buildGraphicGroupFromObj(obj),
		[editor.object_TEXT]: obj => editor.buildTextFromObj(obj),
		[editor.object_ANIMATIONGROUP]: obj => editor.buildAnimationGroupFromObject(obj),
	}[data.type];
	const graphic = build(data);
	container.addChildAt(graphic, Math.min(index, container.children.length - 1));
}

const isSelected = object => game.editor.selectedPhysicsBodies.includes(object) || game.editor.selectedTextures.includes(object);

// Called before every save and undo step: an edited clone passes its changes on to the others.
export const syncClones = () => {
	const nextSignatures = new Map();
	getCloneSets().forEach((objects, id) => {
		const signatures = objects.map(signatureOf);
		const synced = syncedSignatures.get(id);
		let sourceIndex = -1;
		if (signatures.some(signature => !sameShared(signature, signatures[0]))) {
			const pick = test => {
				const candidates = objects.map((object, i) => i).filter(test);
				return candidates.find(i => isSelected(objects[i])) ?? candidates[0] ?? -1;
			}
			sourceIndex = pick(i => syncedInstances.has(rootSprite(objects[i])) && synced !== undefined && !sameShared(signatures[i], synced));
			if (sourceIndex < 0) sourceIndex = pick(i => synced !== undefined && sameShared(signatures[i], synced));
			if (sourceIndex < 0) sourceIndex = pick(i => syncedInstances.has(rootSprite(objects[i])));
			if (sourceIndex < 0) sourceIndex = 0;

			const source = objects[sourceIndex];
			objects.forEach((object, i) => {
				if (sameShared(signatures[i], signatures[sourceIndex])) return;
				if (object.mySprite) applyToBody(object, source);
				else applyToGraphicGroup(object, source);
			});
		}
		nextSignatures.set(id, signatures[Math.max(sourceIndex, 0)]);
		objects.forEach(object => syncedInstances.add(rootSprite(object)));
	});
	syncedSignatures = nextSignatures;
}

export const resetCloneSync = () => {
	syncedSignatures = new Map();
	syncedInstances = new WeakSet();
}

// Editing inside a group can move its origin; its clones move theirs the same way so nothing jumps.
export const moveClonesWithOrigin = (object, dx, dy) => {
	const sprite = rootSprite(object);
	const flip = sprite.data.mirrored ? -1 : 1;
	const angle = object.mySprite ? object.GetAngle() : sprite.rotation;
	const localX = (dx * Math.cos(angle) + dy * Math.sin(angle)) * flip;
	const localY = -dx * Math.sin(angle) + dy * Math.cos(angle);
	const [scaleX, scaleY] = scaleOf(object);
	getClones(object).forEach(clone => {
		if (clone === object) return;
		const [cloneScaleX, cloneScaleY] = scaleOf(clone);
		const cloneFlip = rootSprite(clone).data.mirrored ? -1 : 1;
		const cloneX = localX * cloneFlip * cloneScaleX / scaleX;
		const cloneY = localY * cloneScaleY / scaleY;
		const cloneAngle = clone.mySprite ? clone.GetAngle() : clone.rotation;
		const x = cloneX * Math.cos(cloneAngle) - cloneY * Math.sin(cloneAngle);
		const y = cloneX * Math.sin(cloneAngle) + cloneY * Math.cos(cloneAngle);
		if (clone.mySprite) {
			const position = clone.GetPosition();
			vec1.Set(position.get_x() + x, position.get_y() + y);
			clone.SetTransform(vec1, cloneAngle);
			game.editor.updateBodyPosition(clone);
		} else {
			clone.x += x;
			clone.y += y;
		}
	});
}
